/*
 * LWB add-to-cart overlay
 *
 * Shows a branded modal that steps through "Crafting your ring / Setting the
 * details / Finalising your ring / Adding to cart" while the item is actually
 * being added, then navigates to the cart page.
 *
 * The item is added over the Cart AJAX API rather than by letting the form post
 * normally, so the overlay can stay up for the whole sequence and the customer
 * never sees a half-rendered page. If anything fails, the overlay closes and
 * the form is submitted the ordinary way so the customer is never stranded.
 */
(function () {
  'use strict';

  var STEPS = window.LWB_ATC_STEPS || [
    'Crafting your ring',
    'Setting the details',
    'Finalising your ring',
    'Adding to cart'
  ];
  var SUBTITLE = window.LWB_ATC_SUBTITLE || "We're preparing your item and adding it to your cart.";

  /*
   * Pacing. Adding a configured ring takes roughly 4-5s because a variant is
   * created against an external service first. The earlier 700ms tick burned
   * through every message in about two seconds and then sat on the last one,
   * which read as a stall.
   *
   * So the messages before the final one are paced across the expected wait,
   * and the final one is not reached on a timer at all: the caller raises it
   * when the cart request actually starts (see showFinal). The timer holds on
   * the second-to-last message for as long as the real work takes.
   */
  var EXPECTED_MS = window.LWB_ATC_EXPECTED_MS || 4200;
  var STEP_MS = window.LWB_ATC_STEP_MS || 0;

  var overlay, titleEl, subEl, stepTimer, stepIndex;

  function build() {
    if (overlay) return overlay;

    overlay = document.createElement('div');
    overlay.className = 'lwb-atc';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-live', 'polite');
    overlay.setAttribute('aria-modal', 'true');
    overlay.hidden = true;
    overlay.innerHTML =
      '<div class="lwb-atc__card">' +
        '<div class="lwb-atc__spinner" aria-hidden="true"></div>' +
        '<p class="lwb-atc__title"></p>' +
        '<p class="lwb-atc__sub"></p>' +
      '</div>';

    document.body.appendChild(overlay);
    titleEl = overlay.querySelector('.lwb-atc__title');
    subEl = overlay.querySelector('.lwb-atc__sub');
    return overlay;
  }

  function open() {
    build();
    stepIndex = 0;
    titleEl.textContent = STEPS[0];
    subEl.textContent = SUBTITLE;
    overlay.hidden = false;
    document.body.style.overflow = 'hidden';

    // Spread the messages before the final one across the expected wait, then
    // hold. The final message is raised by showFinal() when the cart request
    // actually starts, so it reflects real progress rather than a timer.
    var paced = Math.max(1, STEPS.length - 1);
    var tick = STEP_MS || Math.round(EXPECTED_MS / paced);

    stepTimer = setInterval(function () {
      if (stepIndex < STEPS.length - 2) {
        stepIndex += 1;
        titleEl.textContent = STEPS[stepIndex];
      }
    }, tick);
  }

  /* Jump to the last message. Called when the add request is genuinely underway. */
  function showFinal() {
    if (!overlay || overlay.hidden) return;
    clearInterval(stepTimer);
    stepIndex = STEPS.length - 1;
    titleEl.textContent = STEPS[stepIndex];
  }

  function close() {
    clearInterval(stepTimer);
    if (overlay) overlay.hidden = true;
    document.body.style.overflow = '';
  }

  /*
   * Public API. The ring configurator has its own add-to-cart path that never
   * submits a form, so the submit listener below never sees it. It drives the
   * overlay through these instead. Optionally accepts its own step labels.
   */
  window.LWBAtc = {
    open: function (steps) {
      if (Array.isArray(steps) && steps.length) STEPS = steps;
      open();
    },
    showFinal: showFinal,
    close: close
  };

  /*
   * Back-forward cache.
   *
   * The overlay is deliberately left up while navigating to /cart, so the
   * customer never sees the product page again mid-transition. But iOS Safari
   * restores a page from bfcache with the DOM exactly as it was left: tapping
   * Back from the cart came back to the product page stuck behind "Adding to
   * cart", with body scroll still locked and the submit button aria-disabled.
   *
   * pageshow fires on bfcache restore (event.persisted) where load/DOMContent-
   * Loaded do not, so it is the only reliable hook for undoing that state.
   */
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted && (!overlay || overlay.hidden)) return;

    close();

    var disabled = document.querySelectorAll('[type="submit"][aria-disabled="true"]');
    for (var i = 0; i < disabled.length; i += 1) {
      disabled[i].removeAttribute('aria-disabled');
    }
  });

  /* Minimum on-screen time so the sequence doesn't flash by on a fast add. */
  function settle(startedAt) {
    var elapsed = Date.now() - startedAt;
    var minimum = window.LWB_ATC_MIN_MS || 1200;
    return new Promise(function (resolve) {
      setTimeout(resolve, Math.max(0, minimum - elapsed));
    });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;

    // Only product add-to-cart forms.
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;
    if (form.dataset.lwbSkipOverlay === 'true') return;

    event.preventDefault();

    var button = form.querySelector('[type="submit"]');
    if (button) button.setAttribute('aria-disabled', 'true');

    var startedAt = Date.now();
    open();

    var body = new FormData(form);

    fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/javascript' },
      body: body
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw data;
          return data;
        });
      })
      .then(function (data) {
        showFinal();
        return settle(startedAt).then(function () { return data; });
      })
      .then(function () {
        window.location.href =
          (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart';
      })
      .catch(function (err) {
        close();
        if (button) button.removeAttribute('aria-disabled');

        // Shopify returns a description for sold-out / quantity errors.
        var message = err && (err.description || err.message);
        if (message) {
          window.alert(message);
          return;
        }
        // Unknown failure: fall back to a normal post so the customer can still buy.
        form.dataset.lwbSkipOverlay = 'true';
        form.submit();
      });
  });
})();
